import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Badge,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  message,
  Popover,
  Select,
  Space,
  Tabs,
} from 'antd';
import dayjs from 'dayjs';
import { debounce, uniqBy } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../AppContext';
import {
  ATTENDANCE_TRACKER_TABLE_FIELDS,
  LIMIT,
  MODULES_KEY,
  PERMISSION_OBJECT,
  ROLE_KEYS,
} from '../../../common/constants';
import CommonSelect from '../../../components/CommonSelect';
import VerifyPermissions from '../../../components/VerifyPermissions';
import {
  mapAntTableChangeToParams,
  useTableQueryParams,
} from '../../../hooks/useTableQueryParams';
import { DEPARTMENTS, USER_LEVEL } from '../../users/graphql/Queries';
import AttendanceTrackerView from './AttendanceTrackerView';
import {
  BULK_INCIDENT_ARCHIVE,
  BULK_INCIDENT_EXCUSE,
  INCIDENT_ARCHIVE,
  INCIDENT_EXCUSE,
  LOG_INCIDENT,
} from './graphql/Mutations';
import {
  ATTENDANCE_INCIDENTS,
  ATTENDANCE_SCORE,
  INCIDENT_DETAIL,
  MY_ATTENDANCE_INCIDENTS,
  PODS,
  TEAM_ATTENDANCE,
  TEAMS_QUATRES,
  USER_ATTENDANCE,
  USERS_LISTING,
} from './graphql/Queries';
import IncidentBreakdownModal from './IncidentBreakdownModal';
import IncidentHistoryDrawer from './IncidentHistoryDrawer';
import IncidentLogModal from './IncidentLogModal';
import IncidentsListView from './IncidentsListView';
import ManageIncidentModal from './ManageIncidentModal';
import MyAttendanceView from './MyAttendanceView';
import PeriodBreakdownModal from './PeriodBreakdownModal';
import ScoreBreakdownModal from './ScoreBreakdownModal';
import UserAttendanceView from './UserAttendanceView';

const { RangePicker } = DatePicker;

// Quarter/Year/My Attendance are kept in code (behind `hidden: true`) rather
// than deleted, so they can be restored by flipping this flag without
// rewriting the tab wiring.
const PERIOD_TABS = [
  { key: 'MONTH', label: 'Month' },
  { key: 'QUARTER', label: 'Quarter', hidden: true },
  { key: 'YEAR', label: 'Year', hidden: true },
  { key: 'MY', label: 'My Attendance', hidden: true },
  { key: 'USER', label: 'User' },
  { key: 'INCIDENTS', label: 'Tracking Incident' },
];

/** 'YYYY-MM' for a tenureQuarters { month, year } bound. */
const toYearMonth = ({ month, year }) =>
  dayjs()
    .year(year)
    .month(month - 1)
    .format('YYYY-MM');

/** The selected employee's current tenure quarter, as { from, to } 'YYYY-MM'
 * bounds — null if the employee isn't loaded yet or has no current quarter. */
const currentTenureQuarterRange = (employeeOptions, employeeId) => {
  const employee = employeeOptions?.find((option) => option?.id === employeeId);
  const currentQuarter = employee?.tenureQuarters?.find(
    (quarter) => quarter?.isCurrent,
  );
  if (!currentQuarter) return null;
  return {
    from: toYearMonth(currentQuarter.rangeFrom),
    to: toYearMonth(currentQuarter.rangeTo),
  };
};

const toSkip = (page, pageSize) => (page - 1) * pageSize;

const getEmployeeName = (employee) =>
  `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim();

function AttendanceTracker() {
  const {
    state: { currentUser },
  } = useContext(AppContext);
  const currentUserId = currentUser?.id;

  const isHrOrCeo =
    currentUser?.role === ROLE_KEYS.CEO_CTO ||
    currentUser?.role === ROLE_KEYS.SUPER_ADMIN ||
    VerifyPermissions({
      modulekey: 'LEAVES', // HR typically has leaves access
      allowedPermissions: [PERMISSION_OBJECT.read],
    });

  const isCreatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.AOS_ATTENDANCE_TRACKER,
    allowedPermissions: [PERMISSION_OBJECT?.create],
  });

  const isUpdatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.AOS_ATTENDANCE_TRACKER,
    allowedPermissions: [PERMISSION_OBJECT?.update],
  });

  // ── Period / filter / pagination state (synced to URL query params) ───────
  const { params, setParams } = useTableQueryParams({
    fields: ATTENDANCE_TRACKER_TABLE_FIELDS,
  });
  const activePeriod = params?.view;
  const selectedPodId = params?.podId;
  const selectedSearch = params?.search;
  const levelFilteredId = params?.levelIds;
  const selectedQuarter = params?.quarter;
  const selectedMonth = params?.month;
  const incidentMonthRange = params?.incidentMonthRange;
  const selectedIncidentEmployeeId = params?.incidentEmployeeId;
  const selectedIncidentPodId = params?.incidentPodId;
  const selectedUserEmployeeId = params?.userEmployeeId;
  const userDateRange = params?.userDateRange;
  // The Incidents tab keeps its own page/pageSize (separate from the other
  // tabs' shared `page`/`pageSize`) so its pagination survives switching
  // tabs and back.
  const paginationFilter = {
    currentPage:
      activePeriod === 'INCIDENTS' ? params?.incidentPage : params?.page,
    pageSize:
      activePeriod === 'INCIDENTS'
        ? params?.incidentPageSize
        : params?.pageSize,
  };

  // ── User tab: employee search state (transient, not URL-synced) ──────────
  const [userEmployeeSearch, setUserEmployeeSearch] = useState('');
  // Loaded employee options (incl. tenureQuarters), kept in sync via
  // CommonSelect's getApiData so the current-quarter range can be looked up
  // by id when an employee is selected.
  const [userEmployeeOptions, setUserEmployeeOptions] = useState([]);

  // ── Incidents tab: employee search state (transient, not URL-synced) ─────
  const [incidentEmployeeSearch, setIncidentEmployeeSearch] = useState('');

  // ── Name/Department/Designation search (transient dropdown search text,
  //    not URL-synced) ─────────────────────────────────────────────────────
  const [searchTextFilter, setSearchTextFilter] = useState('');

  // ── Pod filter search / pagination (transient, not URL-synced) ────────────
  const [podOptions, setPodOptions] = useState([]);
  const [podSearch, setPodSearch] = useState('');
  const [podSkip, setPodSkip] = useState(0);
  const [isPodDataEnd, setIsPodDataEnd] = useState(false);

  // ── Incident log modal ────────────────────────────────────────────────────
  const [logForm] = Form.useForm();
  const [showLogModal, setShowLogModal] = useState(false);

  // ── Manage Incident modal (Excuse + Archive merged, edit action) ──────────
  const [manageForm] = Form.useForm();
  const [showManageModal, setShowManageModal] = useState(false);
  const [manageTarget, setManageTarget] = useState(null); // { incidentId, rawCount, excusedCount, effectiveCount, isExcused, excuseReason, isArchived, archiveReason }

  // ── Score breakdown modal ─────────────────────────────────────────────────
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [scoreEmployeeName, setScoreEmployeeName] = useState('');

  // ── Period breakdown modal (Quarter → month-wise, Year → quarter-wise) ────
  const [showBreakdownModal, setShowBreakdownModal] = useState(false);
  const [breakdownContext, setBreakdownContext] = useState(null); // { employeeName, behaviourTitle, periodType, rows }

  // ── Incident breakdown modal (User tab month header info icon) ───────────
  const [showIncidentBreakdownModal, setShowIncidentBreakdownModal] = useState(
    false,
  );
  const [incidentBreakdownContext, setIncidentBreakdownContext] = useState(
    null,
  ); // { employeeName, monthLabel, month, year }
  const [incidentBreakdownPage, setIncidentBreakdownPage] = useState(1);
  // Last successfully fetched page for the currently-open month — { data,
  // count }. Kept in place while the next page's fetch is in flight (rather
  // than reading straight off the lazy query's `data`, which goes undefined
  // mid-fetch under fetchPolicy: 'network-only') so the table/pagination
  // show a spinner over the existing page instead of flashing empty and
  // collapsing Pagination to 0 pages between requests. Reset to null when
  // the modal is (re)opened so a new month never briefly shows the old one's
  // rows.
  const [incidentBreakdownDisplay, setIncidentBreakdownDisplay] = useState(
    null,
  );

  // ── Incident history drawer ───────────────────────────────────────────────
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);
  const [historyEmployeeId, setHistoryEmployeeId] = useState(null);
  const [historyEmployeeName, setHistoryEmployeeName] = useState('');
  const [historyYear, setHistoryYear] = useState(dayjs().year());
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(LIMIT);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // ── Quarters query (for filter dropdown) ─────────────────────────────────
  const [fetchQuarters, { data: quartersData }] = useLazyQuery(TEAMS_QUATRES, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  // ── Pods query (for filter dropdown) ─────────────────────────────────────
  const [fetchPods, { loading: podsLoading }] = useLazyQuery(PODS, {
    fetchPolicy: 'network-only',
    onCompleted(res) {
      const pods = res?.pods?.pods ?? [];
      const isDataEnd = pods?.length < LIMIT;
      setIsPodDataEnd(isDataEnd);
      const newData = pods?.map((pod) => ({
        value: pod?.id,
        label: pod?.name,
      }));
      setPodOptions((prevData) =>
        uniqBy([...prevData, ...(newData ?? [])], 'value'),
      );
    },
    onError() {},
  });

  // ── Team attendance query ─────────────────────────────────────────────────
  const [
    fetchTeamAttendance,
    { loading: tableLoading, data: tableData },
  ] = useLazyQuery(TEAM_ATTENDANCE, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  // ── My attendance query (own incidents, read-only) ─────────────────────────
  const [
    fetchMyIncidents,
    { loading: myIncidentsLoading, data: myIncidentsData },
  ] = useLazyQuery(MY_ATTENDANCE_INCIDENTS, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  // ── Score breakdown query ────────────────────────────────────────────────
  const [
    fetchAttendanceScore,
    { loading: scoreLoading, data: scoreData },
  ] = useLazyQuery(ATTENDANCE_SCORE, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  // ── Incident history query (one employee) ────────────────────
  const [
    fetchAttendanceIncidents,
    { loading: historyLoading, data: historyData },
  ] = useLazyQuery(ATTENDANCE_INCIDENTS, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  // ── Incidents tab query (flat list, all employees) ───────────────────────
  const [
    fetchIncidentsList,
    { loading: incidentsListLoading, data: incidentsListData },
  ] = useLazyQuery(ATTENDANCE_INCIDENTS, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  // ── Incident detail query (full detail for the Manage Incident modal) ────
  const [
    fetchIncidentDetail,
    { loading: incidentDetailLoading, data: incidentDetailData },
  ] = useLazyQuery(INCIDENT_DETAIL, {
    fetchPolicy: 'network-only',
    onError(err) {
      message.error(err?.message || 'Failed to load incident details');
    },
  });

  // ── User tab query (one employee, across a month range) ──────────────────
  const [
    fetchUserAttendance,
    { loading: userTabLoading, data: userAttendanceData },
  ] = useLazyQuery(USER_ATTENDANCE, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  // ── User tab: incident breakdown query (one employee, one month) ─────────
  const [
    fetchIncidentBreakdown,
    { loading: incidentBreakdownLoading },
  ] = useLazyQuery(ATTENDANCE_INCIDENTS, {
    fetchPolicy: 'network-only',
    onCompleted(res) {
      setIncidentBreakdownDisplay(
        res?.attendanceIncidents || { data: [], count: 0 },
      );
    },
    onError() {},
  });

  // ── Build query variables (defined before mutations so onCompleted can call refetchTable) ──
  const selectedMonthValue = selectedMonth
    ? dayjs(selectedMonth, 'YYYY-MM')
    : null;
  const incidentMonthFromValue = incidentMonthRange?.from
    ? dayjs(incidentMonthRange.from, 'YYYY-MM')
    : null;
  const incidentMonthToValue = incidentMonthRange?.to
    ? dayjs(incidentMonthRange.to, 'YYYY-MM')
    : null;

  const buildFilters = (pagination = paginationFilter) => ({
    view: activePeriod,
    podId: selectedPodId || undefined,
    search: selectedSearch?.length ? selectedSearch.join(' ') : undefined,
    levelIds: levelFilteredId?.length ? levelFilteredId : undefined,
    quarter: selectedQuarter || undefined,
    month:
      activePeriod === 'MONTH' && selectedMonthValue?.isValid()
        ? selectedMonthValue.month() + 1
        : undefined,
    year:
      activePeriod === 'MONTH' && selectedMonthValue?.isValid()
        ? selectedMonthValue.year()
        : undefined,
    skip: toSkip(pagination.currentPage, pagination.pageSize),
    limit: pagination.pageSize,
  });

  const buildMyFilters = (pagination = paginationFilter) => ({
    year: dayjs().year(),
    skip: toSkip(pagination.currentPage, pagination.pageSize),
    limit: pagination.pageSize,
  });

  const buildIncidentsFilters = (pagination = paginationFilter) => ({
    employeeId: selectedIncidentEmployeeId || undefined,
    podIds: selectedIncidentPodId ? [selectedIncidentPodId] : undefined,
    loggedAtFrom: incidentMonthFromValue?.isValid()
      ? incidentMonthFromValue.startOf('month').toISOString()
      : undefined,
    loggedAtTo: incidentMonthToValue?.isValid()
      ? incidentMonthToValue.endOf('month').toISOString()
      : undefined,
    skip: toSkip(pagination.currentPage, pagination.pageSize),
    limit: pagination.pageSize,
  });

  const refetchTable = (pagination = paginationFilter) => {
    if (activePeriod === 'MY') {
      fetchMyIncidents({ variables: { filter: buildMyFilters(pagination) } });
      return;
    }
    if (activePeriod === 'USER') {
      // Fetched separately below (spans a user-selected month range/employee).
      return;
    }
    if (activePeriod === 'INCIDENTS') {
      fetchIncidentsList({
        variables: {
          filter: buildIncidentsFilters(pagination),
          sort: [
            {
              sortOn: 'createdAt',
              sortBy: 'DESC',
            },
          ],
        },
      });
      return;
    }
    fetchTeamAttendance({ variables: { filter: buildFilters(pagination) } });
  };

  // ── User tab: fetch one employee's attendance across the selected month
  //    range. ─────────────────────────────────────────────────────────────
  const fetchUserTabAttendance = (employeeId, from, to) => {
    if (!employeeId || !from || !to) return;
    const fromDate = dayjs(from, 'YYYY-MM');
    const toDate = dayjs(to, 'YYYY-MM');
    if (!fromDate.isValid() || !toDate.isValid()) return;
    fetchUserAttendance({
      variables: {
        filter: {
          employeeId,
          rangeFrom: { year: fromDate.year(), month: fromDate.month() + 1 },
          rangeTo: { year: toDate.year(), month: toDate.month() + 1 },
        },
      },
    });
  };

  const handleTableChange = (pagination) => {
    const mapped = mapAntTableChangeToParams(
      pagination,
      undefined,
      paginationFilter.pageSize,
    );
    if (activePeriod === 'INCIDENTS') {
      setParams({
        incidentPage: mapped.page,
        incidentPageSize: mapped.pageSize,
      });
      return;
    }
    setParams(mapped);
  };

  // ── Helper: fetch a page of an employee's incident history ───────────────
  const fetchHistoryPage = (employeeId, year, page = 1, pageSize = LIMIT) =>
    fetchAttendanceIncidents({
      variables: {
        filter: {
          employeeId,
          month:
            activePeriod === 'MONTH' && selectedMonthValue?.isValid()
              ? selectedMonthValue.month() + 1
              : undefined,
          year,
          skip: toSkip(page, pageSize),
          limit: pageSize,
        },
        sort: [
          {
            sortOn: 'loggedAt',
            sortBy: 'DESC',
          },
        ],
      },
    });

  // ── Mutations ─────────────────────────────────────────────────────────────
  const [logIncident, { loading: logging }] = useMutation(LOG_INCIDENT, {
    onCompleted() {
      message.success('Incident logged successfully');
      setShowLogModal(false);
      refetchTable();
    },
    onError(err) {
      message.error(err?.message || 'Failed to log incident');
    },
  });

  // The Manage Incident modal's tabs are mutually exclusive per save (only
  // the active tab's action fires), so these are orchestrated together in
  // handleManageSave rather than each closing the modal / refetching on
  // their own.
  const [setIncidentExcuseStatus, { loading: excusing }] = useMutation(
    INCIDENT_EXCUSE,
    {
      onError(err) {
        message.error(err?.message || 'Failed to update excuse status');
      },
    },
  );

  const [setIncidentArchiveStatus, { loading: archiving }] = useMutation(
    INCIDENT_ARCHIVE,
    {
      onError(err) {
        message.error(err?.message || 'Failed to update incident');
      },
    },
  );

  // ── Bulk mutations: Incident History drawer's multi-select Genuine/Archive
  //    actions. ───────────────────────────────────────────────────────────
  const [bulkSetIncidentExcuseStatus] = useMutation(BULK_INCIDENT_EXCUSE, {
    onError(err) {
      message.error(err?.message || 'Failed to update excuse status');
    },
  });

  const [bulkSetIncidentArchiveStatus] = useMutation(BULK_INCIDENT_ARCHIVE, {
    onError(err) {
      message.error(err?.message || 'Failed to update incident');
    },
  });

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchPods({ variables: { filters: { skip: 0, limit: LIMIT } } });
    fetchQuarters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The selected pod may live beyond the first page (e.g. it was picked after
  // scrolling the dropdown before navigating away) — a refresh remounts this
  // page and resets the loaded list to page one, so without this the Select
  // can't resolve a label for the saved id and falls back to showing the raw
  // id. Keep paginating until the selected pod is found. Pod options are
  // shared between the Month/Quarter/Year filter and the Incidents tab
  // filter (only one is ever mounted at a time), so this resolves whichever
  // one is actually active.
  const activePodIdToResolve =
    activePeriod === 'INCIDENTS' ? selectedIncidentPodId : selectedPodId;
  useEffect(() => {
    if (
      !activePodIdToResolve ||
      podOptions?.length === 0 ||
      isPodDataEnd ||
      podsLoading
    )
      return;
    const isSelectedPodLoaded = podOptions?.some(
      (pod) => pod?.value === activePodIdToResolve,
    );
    if (isSelectedPodLoaded) return;
    const newSkip = podSkip + LIMIT;
    fetchPods({
      variables: {
        filters: {
          skip: newSkip,
          limit: LIMIT,
          searchString: podSearch || undefined,
        },
      },
    });
    setPodSkip(newSkip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podOptions, activePodIdToResolve, isPodDataEnd, podsLoading]);

  useEffect(() => {
    refetchTable(paginationFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params?.view,
    params?.podId,
    params?.search,
    params?.levelIds,
    params?.quarter,
    params?.month,
    params?.incidentMonthRange?.from,
    params?.incidentMonthRange?.to,
    params?.incidentEmployeeId,
    params?.incidentPodId,
    params?.page,
    params?.pageSize,
    params?.incidentPage,
    params?.incidentPageSize,
  ]);

  // Default the Month tab's month filter to the current month the first
  // time it's opened (i.e. while none is selected yet); the user can change
  // it freely afterwards.
  useEffect(() => {
    if (activePeriod === 'MONTH' && !selectedMonth) {
      setParams({ month: dayjs().format('YYYY-MM') });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePeriod]);

  useEffect(() => {
    if (activePeriod !== 'USER') return;
    fetchUserTabAttendance(
      selectedUserEmployeeId,
      userDateRange?.from,
      userDateRange?.to,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activePeriod,
    selectedUserEmployeeId,
    userDateRange?.from,
    userDateRange?.to,
  ]);

  // ── Handler: open period breakdown modal (Quarter → months, Year → quarters) ──
  const handleOpenBreakdown = (
    employeeRow,
    fieldKey,
    behaviourTitle,
    breakdownKey,
  ) => {
    const periodType =
      breakdownKey === 'monthlyBreakdown' ? 'MONTH' : 'QUARTER';
    let items = employeeRow?.[breakdownKey] || [];
    if (breakdownKey === 'quarterlyBreakdown') {
      items = items.slice(-4);
    }
    const rows = items.map((item) => ({
      key: periodType === 'MONTH' ? item.month : item.quarter,
      label:
        periodType === 'MONTH'
          ? item.monthName
          : item.quarterLabel || `Q${item.quarter} ${item.year}`,
      ...(item?.[fieldKey] || {}),
    }));
    setBreakdownContext({
      employeeName: getEmployeeName(employeeRow.employee),
      behaviourTitle,
      periodType,
      rows,
    });
    setShowBreakdownModal(true);
  };

  // ── Handler: table count click — routes to breakdown modal on Quarter/Year
  //    tabs. Excusing/archiving is handled via the Manage Incident modal
  //    (edit action) instead, not from this click. ──────────────────────────
  const handleCountClick = (
    employeeRow,
    incident,
    // eslint-disable-next-line no-unused-vars
    { fieldKey, behaviourKey, title },
  ) => {
    if (activePeriod === 'QUARTER') {
      handleOpenBreakdown(employeeRow, fieldKey, title, 'monthlyBreakdown');
      return;
    }
    if (activePeriod === 'YEAR') {
      handleOpenBreakdown(employeeRow, fieldKey, title, 'quarterlyBreakdown');
    }
  };

  // ── Handler: open score breakdown modal from the score badge ─────────────
  const handleScoreClick = (record) => {
    setScoreEmployeeName(getEmployeeName(record.employee));
    setShowScoreModal(true);
    fetchAttendanceScore({
      variables: {
        where: {
          employeeId: record.employeeId,
          view: activePeriod,
          month: record.month ?? undefined,
          year: record.year ?? undefined,
        },
      },
    });
  };

  // ── Helper: fetch a page of the incident breakdown modal's incidents ─────
  const fetchIncidentBreakdownPage = (month, year, page) => {
    fetchIncidentBreakdown({
      variables: {
        filter: {
          employeeId: selectedUserEmployeeId,
          month,
          year,
          skip: toSkip(page, LIMIT),
          limit: LIMIT,
          isArchived: false,
          isExcused: false,
        },
        sort: [
          {
            sortOn: 'loggedAt',
            sortBy: 'DESC',
          },
        ],
      },
    });
  };

  // ── Handler: open incident breakdown modal from a User-tab month header's
  //    info icon ───────────────────────────────────────────────────────────
  const handleIncidentInfoClick = (monthEntry) => {
    const employee = userAttendanceData?.userAttendance?.data?.[0]?.employee;
    setIncidentBreakdownContext({
      employeeId: employee?.id,
      employeeName: getEmployeeName(employee),
      monthLabel: dayjs(`${monthEntry.year}-${monthEntry.month}-01`).format(
        'MMMM YYYY',
      ),
      finalScore: monthEntry.score,
      month: monthEntry.month,
      year: monthEntry.year,
    });
    setShowIncidentBreakdownModal(true);
    setIncidentBreakdownPage(1);
    setIncidentBreakdownDisplay(null);
    fetchIncidentBreakdownPage(monthEntry.month, monthEntry.year, 1);
  };

  // ── Handler: incident breakdown modal — pagination change ────────────────
  const handleIncidentBreakdownTableChange = (pagination) => {
    const page = pagination?.current || 1;
    setIncidentBreakdownPage(page);
    fetchIncidentBreakdownPage(
      incidentBreakdownContext?.month,
      incidentBreakdownContext?.year,
      page,
    );
  };

  // ── Handler: open incident history drawer from the employee cell ─────────
  const handleViewHistory = (record) => {
    const year = record.year || dayjs().year();
    setHistoryEmployeeId(record.employeeId);
    setHistoryEmployeeName(getEmployeeName(record.employee));
    setHistoryYear(year);
    setHistoryPage(1);
    setHistoryPageSize(LIMIT);
    setShowHistoryDrawer(true);
    fetchHistoryPage(record.employeeId, year, 1, LIMIT);
  };

  // ── Handler: incident history drawer — pagination change ─────────────────
  const handleHistoryTableChange = (pagination) => {
    const { page, pageSize } = mapAntTableChangeToParams(
      pagination,
      undefined,
      historyPageSize,
    );
    setHistoryPage(page);
    setHistoryPageSize(pageSize);
    fetchHistoryPage(historyEmployeeId, historyYear, page, pageSize);
  };

  // ── Handler: log incident form submit ─────────────────────────────────────
  const handleLogSubmit = (values) => {
    const incidentDate = values.date;
    logIncident({
      variables: {
        data: {
          employeeId: values.employeeId,
          date: incidentDate.toISOString(),
          behaviourKey: values.behaviour,
          incrementBy: values.count,
          reason: values.reason || undefined,
        },
      },
    });
  };

  // ── Handler: open the Manage Incident modal from the edit action ─────────
  const handleOpenManage = (record) => {
    const excusedCount = record?.excusedCount ?? 0;
    const incidentEmployeeId = record?.employeeId ?? record?.employee?.id;
    setManageTarget({
      incidentId: record?.id,
      rawCount: record?.rawCount ?? 0,
      excusedCount,
      effectiveCount: record?.effectiveCount ?? 0,
      isExcused: excusedCount > 0,
      excuseReason: record?.excuseReason,
      isArchived: record?.isArchived ?? false,
      archiveReason: record?.archiveReason,
      // A user must not be able to update their own incident, even with
      // update permission.
      isOwnIncident: !!currentUserId && incidentEmployeeId === currentUserId,
    });
    setShowManageModal(true);
    fetchIncidentDetail({ variables: { where: { id: record?.id } } });
  };

  // ── Handler: Manage Incident modal save — only the active tab's action
  //    (Excuse or Archive) is submitted, never both. ───────────────────────
  const handleManageSave = async (values) => {
    try {
      if (values.action === 'excuse') {
        await setIncidentExcuseStatus({
          variables: {
            where: { id: manageTarget?.incidentId },
            isExcused: values.isExcused,
            reason: values.excuseReason || undefined,
          },
        });
      } else {
        await setIncidentArchiveStatus({
          variables: {
            where: { id: manageTarget?.incidentId },
            isArchived: values.isArchived,
            reason: values.archiveReason || '',
          },
        });
      }
      setShowManageModal(false);
      refetchTable();
      if (showHistoryDrawer && historyEmployeeId) {
        fetchHistoryPage(
          historyEmployeeId,
          historyYear,
          historyPage,
          historyPageSize,
        );
      }
    } catch {
      // Per-mutation onError already surfaced a toast for whichever failed.
    }
  };

  // ── Handler: Incident History drawer — bulk Excuse/Archive across the
  //    selected rows, via the bulkSetIncidentExcuseStatus /
  //    bulkSetIncidentArchiveStatus mutations (single request for the whole
  //    selection rather than one per incident). ─────────────────────────────
  const handleBulkIncidentAction = async ({
    action,
    ids,
    isExcused,
    isArchived,
    reason,
  }) => {
    if (!ids?.length) return;
    setBulkUpdating(true);
    try {
      if (action === 'excuse') {
        await bulkSetIncidentExcuseStatus({
          variables: { where: { ids }, isExcused, reason },
        });
      } else {
        await bulkSetIncidentArchiveStatus({
          variables: { where: { ids }, isArchived, reason },
        });
      }
      refetchTable();
      if (showHistoryDrawer && historyEmployeeId) {
        // Awaited so the drawer only clears its row selection once the
        // refreshed page has actually loaded, instead of unchecking rows
        // while the stale pre-mutation data is still on screen.
        await fetchHistoryPage(
          historyEmployeeId,
          historyYear,
          historyPage,
          historyPageSize,
        );
      }
    } catch {
      // Mutation onError already surfaced a toast for the failure.
      throw new Error('Failed to update incidents');
    } finally {
      setBulkUpdating(false);
    }
  };

  // ── Handler: pod filter ───────────────────────────────────────────────────
  const handlePod = (value) => {
    setParams({ podId: value || null, page: 1 });
    // Reset the search so the dropdown's list resets to the full (unfiltered)
    // first page next time it's opened — both on clear and after selecting a
    // pod found via search — instead of still showing only the last search's
    // results.
    setPodSearch('');
    setPodSkip(0);
    setPodOptions([]);
    fetchPods({ variables: { filters: { skip: 0, limit: LIMIT } } });
  };

  const handlePodSearch = debounce((value) => {
    setPodSearch(value);
    setPodSkip(0);
    setPodOptions([]);
    fetchPods({
      variables: {
        filters: { skip: 0, limit: LIMIT, searchString: value || undefined },
      },
    });
  }, 500);

  const handlePodPopupScroll = async (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e?.target || {};
    const nearBottom = scrollTop + clientHeight >= scrollHeight - 5;
    if (nearBottom && !isPodDataEnd) {
      const newSkip = podSkip + LIMIT;
      await fetchPods({
        variables: {
          filters: {
            skip: newSkip,
            limit: LIMIT,
            searchString: podSearch || undefined,
          },
        },
      });
      setPodSkip(newSkip);
    }
  };

  // ── Handler: Name/Department/Designation search filter ───────────────────
  const handleSearchFilter = (value) => {
    setParams({ search: value, page: 1 });
    setSearchTextFilter('');
  };

  const handleSearchFilterSearch = debounce((value) => {
    setSearchTextFilter(value);
  }, 500);

  // ── Handler: level filter ────────────────────────────────────────────────
  const handleLevel = (value) => {
    setParams({ levelIds: value, page: 1 });
  };

  // ── Handler: quarter filter ──────────────────────────────────────────────
  const handleQuarter = (value) => {
    setParams({ quarter: value || null, page: 1 });
  };

  // ── Handler: User tab — employee filter ──────────────────────────────────
  const handleUserEmployeeChange = (value) => {
    const currentQuarterRange = value
      ? currentTenureQuarterRange(userEmployeeOptions, value)
      : null;
    setParams({
      userEmployeeId: value || null,
      ...(currentQuarterRange ? { userDateRange: currentQuarterRange } : {}),
    });
    // Reset the search text so the dropdown's underlying list resets to the
    // full (unfiltered) first page next time it's opened, instead of still
    // showing only the results from whatever was last searched.
    setUserEmployeeSearch('');
  };

  const handleUserEmployeeSearch = debounce((value) => {
    setUserEmployeeSearch(value);
  }, 500);

  // ── Handler: User tab — date range filter ────────────────────────────────
  const handleUserDateRangeChange = (dates) => {
    if (!dates?.[0] || !dates?.[1]) {
      setParams({ userDateRange: { from: null, to: null } });
      return;
    }
    setParams({
      userDateRange: {
        from: dates[0].format('YYYY-MM'),
        to: dates[1].format('YYYY-MM'),
      },
    });
  };

  // ── Handler: Month tab — month filter ────────────────────────────────────
  const handleMonthChange = (date) => {
    setParams({ month: date ? date.format('YYYY-MM') : null, page: 1 });
  };

  // ── Handler: Incidents tab — month filter ─────────────────────────────────
  const handleIncidentMonthRangeChange = (dates) => {
    if (!dates?.[0] || !dates?.[1]) {
      setParams({
        incidentMonthRange: { from: null, to: null },
        incidentPage: 1,
      });
      return;
    }
    setParams({
      incidentMonthRange: {
        from: dates[0].format('YYYY-MM'),
        to: dates[1].format('YYYY-MM'),
      },
      incidentPage: 1,
    });
  };

  // ── Handler: Incidents tab — employee filter ─────────────────────────────
  const handleIncidentEmployeeChange = (value) => {
    setParams({ incidentEmployeeId: value || null, incidentPage: 1 });
    // Reset the search text so the dropdown's underlying list resets to the
    // full (unfiltered) first page next time it's opened, instead of still
    // showing only the results from whatever was last searched.
    setIncidentEmployeeSearch('');
  };

  const handleIncidentEmployeeSearch = debounce((value) => {
    setIncidentEmployeeSearch(value);
  }, 500);

  // ── Handler: Incidents tab — pod filter ──────────────────────────────────
  const handleIncidentPodChange = (value) => {
    setParams({ incidentPodId: value || null, incidentPage: 1 });
    // Reset the search so the dropdown's list resets to the full
    // (unfiltered) first page next time it's opened.
    setPodSearch('');
    setPodSkip(0);
    setPodOptions([]);
    fetchPods({ variables: { filters: { skip: 0, limit: LIMIT } } });
  };

  // ── Handler: period tab change ───────────────────────────────────────────
  // Incidents tab filters (month range/employee/pod) and its pagination
  // (incidentPage/incidentPageSize) are intentionally left untouched here so
  // they survive switching to another tab and back.
  const handlePeriodChange = (value) => {
    setParams({
      view: value,
      page: 1,
      quarter: value === 'QUARTER' ? selectedQuarter : null,
      month: value === 'MONTH' ? selectedMonth : null,
      userDateRange:
        value === 'USER' ? userDateRange : { from: null, to: null },
      userEmployeeId: value === 'USER' ? selectedUserEmployeeId : null,
    });
  };

  const renderMaxTag = (omittedValues) => {
    if (omittedValues?.length === 0) {
      return null;
    }
    return <span>+ {omittedValues?.length} more...</span>;
  };

  const quarterOptions = (
    quartersData?.employeeTenureQuarters || []
  ).map((item) => ({ value: item?.quarter, label: `Q${item?.quarter}` }));

  const activeFilterCount = [
    activePeriod === 'QUARTER' && !!selectedQuarter,
    !!selectedPodId,
    !!levelFilteredId?.length,
  ]?.filter(Boolean)?.length;

  const handleClearAllFilters = () => {
    setParams({
      quarter: null,
      podId: null,
      levelIds: [],
      page: 1,
    });
    setPodSearch('');
    setPodSkip(0);
    setPodOptions([]);
    fetchPods({ variables: { filters: { skip: 0, limit: LIMIT } } });
  };

  const tabBarExtraContent = () => (
    <Space className="at-tab-extra">
      {activePeriod !== 'INCIDENTS' && (
        <CommonSelect
          mode="tags"
          placeholder="Select Name, Department, Designation"
          className="at-search-select"
          query={DEPARTMENTS}
          onChange={handleSearchFilter}
          onSearch={handleSearchFilterSearch}
          variables={{ search: searchTextFilter }}
          useEffectDeps={[searchTextFilter]}
          responsePath="departments.departments"
          valuePath="name"
          labelPath="name"
          fetchPolicy="network-only"
          maxTagCount={3}
          maxTagPlaceholder={renderMaxTag}
          allowClear
          onClear={() => setParams({ search: [], page: 1 })}
          value={selectedSearch}
          virtual={false}
        />
      )}
      {activePeriod === 'MONTH' && (
        <DatePicker
          picker="month"
          allowClear={false}
          placeholder="Filter by Month"
          format="MMM YYYY"
          value={selectedMonthValue}
          onChange={handleMonthChange}
        />
      )}
      {activePeriod === 'INCIDENTS' && (
        <div className="d-flex gap-8">
          <CommonSelect
            placeholder="Search employee"
            showSearch
            allowClear
            style={{ width: 260 }}
            query={USERS_LISTING}
            responsePath="usersAdminAttendanceLog.users"
            valuePath="id"
            getLabel={(item) => `${item.firstName} ${item.lastName}`}
            onChange={handleIncidentEmployeeChange}
            onSearch={handleIncidentEmployeeSearch}
            variables={{
              filters: {
                searchStrings: incidentEmployeeSearch
                  ? [incidentEmployeeSearch]
                  : [],
              },
            }}
            useEffectDeps={[incidentEmployeeSearch]}
            value={selectedIncidentEmployeeId}
            resolveMissingSelected
            virtual={false}
          />
          <RangePicker
            picker="month"
            allowClear
            placeholder={['From Month', 'To Month']}
            format="MMM YYYY"
            value={[incidentMonthFromValue, incidentMonthToValue]}
            onChange={handleIncidentMonthRangeChange}
          />
          <Select
            allowClear
            showSearch
            style={{ width: 200 }}
            placeholder="Filter by Pod"
            options={podOptions}
            value={selectedIncidentPodId}
            onChange={handleIncidentPodChange}
            onSearch={handlePodSearch}
            onPopupScroll={handlePodPopupScroll}
            filterOption={false}
            virtual={false}
          />
        </div>
      )}
      {activePeriod !== 'INCIDENTS' && (
        <Popover
          placement="bottomRight"
          trigger="click"
          overlayClassName="at-filter-popover"
          title={
            <div className="at-filter-popover-title">
              <span>Filters</span>
              <Button
                type="link"
                size="small"
                disabled={activeFilterCount === 0}
                onClick={handleClearAllFilters}
              >
                Clear All
              </Button>
            </div>
          }
          content={
            <div className="at-filter-list">
              {activePeriod === 'QUARTER' && (
                <div className="at-filter-item">
                  <span className="at-filter-label">Quarter</span>
                  <Select
                    allowClear
                    placeholder="Filter by Quarter"
                    options={quarterOptions}
                    value={selectedQuarter}
                    onChange={handleQuarter}
                    virtual={false}
                  />
                </div>
              )}
              <div className="at-filter-item">
                <span className="at-filter-label">Pod</span>
                <Select
                  allowClear
                  showSearch
                  placeholder="Filter by Pod"
                  options={podOptions}
                  value={selectedPodId}
                  onChange={handlePod}
                  onSearch={handlePodSearch}
                  onPopupScroll={handlePodPopupScroll}
                  filterOption={false}
                  virtual={false}
                />
              </div>
              <div className="at-filter-item">
                <span className="at-filter-label">Level</span>
                <CommonSelect
                  mode="multiple"
                  placeholder="Filter by Level"
                  query={USER_LEVEL}
                  onChange={handleLevel}
                  responsePath="levels.levels"
                  valuePath="id"
                  labelPath="name"
                  fetchPolicy="network-only"
                  maxTagCount={3}
                  maxTagPlaceholder={renderMaxTag}
                  allowClear
                  onClear={() => setParams({ levelIds: [], page: 1 })}
                  value={levelFilteredId}
                  resolveMissingSelected
                  virtual={false}
                />
              </div>
            </div>
          }
        >
          <Badge count={activeFilterCount} size="small" offset={[-4, 4]}>
            <Button icon={<FilterOutlined />}>Filters</Button>
          </Badge>
        </Popover>
      )}
    </Space>
  );

  const tableEmployees = tableData?.teamAttendance?.data || [];
  const myIncidents = myIncidentsData?.myAttendanceIncidents?.data || [];
  const historyIncidents = [
    ...(historyData?.attendanceIncidents?.data || []),
  ].sort((a, b) => dayjs(b.loggedAt).valueOf() - dayjs(a.loggedAt).valueOf());
  const isMyAttendanceTab = activePeriod === 'MY';
  const isUserTab = activePeriod === 'USER';
  const isIncidentsTab = activePeriod === 'INCIDENTS';

  const userDateRangeValue =
    userDateRange?.from && userDateRange?.to
      ? [
          dayjs(userDateRange.from, 'YYYY-MM'),
          dayjs(userDateRange.to, 'YYYY-MM'),
        ]
      : undefined;

  const renderUserTab = () => (
    <div className="user-attendance-wrapper">
      <div className="user-attendance-filters">
        <CommonSelect
          placeholder="Search employee"
          showSearch
          allowClear
          style={{ width: 260 }}
          query={USERS_LISTING}
          responsePath="usersAdminAttendanceLog.users"
          valuePath="id"
          getLabel={(item) => `${item.firstName} ${item.lastName}`}
          getApiData={setUserEmployeeOptions}
          onChange={handleUserEmployeeChange}
          onSearch={handleUserEmployeeSearch}
          variables={{
            filters: {
              searchStrings: userEmployeeSearch ? [userEmployeeSearch] : [],
            },
          }}
          useEffectDeps={[userEmployeeSearch]}
          value={selectedUserEmployeeId}
          resolveMissingSelected
          virtual={false}
        />
        <RangePicker
          picker="month"
          allowClear={false}
          disabled={!selectedUserEmployeeId}
          format="MMM YYYY"
          value={userDateRangeValue}
          onChange={handleUserDateRangeChange}
        />
      </div>
      {selectedUserEmployeeId ? (
        <UserAttendanceView
          data={userAttendanceData?.userAttendance?.data?.[0]?.monthlyBreakdown}
          loading={userTabLoading}
          onInfoClick={handleIncidentInfoClick}
        />
      ) : (
        <Empty description="Select an employee to view their attendance" />
      )}
    </div>
  );

  const renderTableBody = () => {
    if (isMyAttendanceTab) {
      return (
        <MyAttendanceView
          data={myIncidents}
          loading={myIncidentsLoading}
          onChange={handleTableChange}
          paginationConfig={{
            current: paginationFilter.currentPage,
            total: myIncidentsData?.myAttendanceIncidents?.count,
            pageSize: paginationFilter.pageSize,
          }}
        />
      );
    }
    if (isUserTab) {
      return renderUserTab();
    }
    if (isIncidentsTab) {
      return (
        <IncidentsListView
          data={incidentsListData?.attendanceIncidents?.data || []}
          loading={incidentsListLoading}
          onChange={handleTableChange}
          onEdit={handleOpenManage}
          canUpdate={isUpdatePermission}
          currentUserId={currentUserId}
          paginationConfig={{
            current: paginationFilter.currentPage,
            total: incidentsListData?.attendanceIncidents?.count,
            pageSize: paginationFilter.pageSize,
          }}
        />
      );
    }
    return (
      <AttendanceTrackerView
        data={tableEmployees}
        loading={tableLoading}
        view={activePeriod}
        onCountClick={handleCountClick}
        onChange={handleTableChange}
        onScoreClick={handleScoreClick}
        onViewHistory={handleViewHistory}
        canUpdate={isUpdatePermission}
        paginationConfig={{
          current: paginationFilter.currentPage,
          total: tableData?.teamAttendance?.count,
          pageSize: paginationFilter.pageSize,
        }}
      />
    );
  };

  return (
    <div className="table-card-page attendance-tracker">
      <Card
        className="ant-body-scroll"
        title="Attendance Tracker"
        extra={
          isCreatePermission && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowLogModal(true)}
            >
              Log Incident
            </Button>
          )
        }
      >
        {/* Period Tabs */}
        <Tabs
          activeKey={activePeriod}
          onChange={handlePeriodChange}
          items={PERIOD_TABS.filter((tab) => !tab.hidden).map(
            ({ hidden, ...tab }) => tab,
          )}
          tabBarStyle={{ paddingLeft: 24, paddingRight: 24 }}
          tabBarExtraContent={
            isMyAttendanceTab || isUserTab ? null : tabBarExtraContent()
          }
        />

        {/* Table */}
        <div className="card-body-wrapper">{renderTableBody()}</div>
      </Card>

      {/* Log Incident Modal */}
      <IncidentLogModal
        open={showLogModal}
        loading={logging}
        form={logForm}
        isHr={isHrOrCeo}
        onFinish={handleLogSubmit}
        onCancel={() => setShowLogModal(false)}
      />

      {/* Manage Incident Modal (Excuse + Archive merged, edit action) */}
      <ManageIncidentModal
        open={showManageModal}
        loading={excusing || archiving}
        incident={manageTarget}
        detail={incidentDetailData?.getIncidentDetails?.data}
        detailLoading={incidentDetailLoading}
        form={manageForm}
        disabled={!isUpdatePermission || manageTarget?.isOwnIncident}
        onSave={handleManageSave}
        onCancel={() => {
          setShowManageModal(false);
          setManageTarget(null);
        }}
      />

      {/* Score Breakdown Modal */}
      <ScoreBreakdownModal
        open={showScoreModal}
        loading={scoreLoading}
        employeeName={scoreEmployeeName}
        data={scoreData?.attendanceScore?.data}
        onCancel={() => setShowScoreModal(false)}
      />

      {/* Period Breakdown Modal (Quarter tab → month-wise, Year tab → quarter-wise) */}
      <PeriodBreakdownModal
        open={showBreakdownModal}
        loading={tableLoading}
        employeeName={breakdownContext?.employeeName}
        behaviourTitle={breakdownContext?.behaviourTitle}
        periodType={breakdownContext?.periodType}
        rows={breakdownContext?.rows}
        onCancel={() => {
          setShowBreakdownModal(false);
          setBreakdownContext(null);
        }}
      />

      {/* Incident Breakdown Modal (User tab month header info icon) */}
      <IncidentBreakdownModal
        open={showIncidentBreakdownModal}
        loading={incidentBreakdownLoading}
        employeeId={incidentBreakdownContext?.employeeId}
        employeeName={incidentBreakdownContext?.employeeName}
        month={incidentBreakdownContext?.month}
        year={incidentBreakdownContext?.year}
        monthLabel={incidentBreakdownContext?.monthLabel}
        finalScore={incidentBreakdownContext?.finalScore}
        incidents={incidentBreakdownDisplay?.data || []}
        onChange={handleIncidentBreakdownTableChange}
        paginationConfig={{
          current: incidentBreakdownPage,
          total: incidentBreakdownDisplay?.count,
          pageSize: LIMIT,
          showSizeChanger: false,
        }}
        onCancel={() => {
          setShowIncidentBreakdownModal(false);
          setIncidentBreakdownContext(null);
          setIncidentBreakdownPage(1);
          setIncidentBreakdownDisplay(null);
        }}
      />

      {/* Incident History Drawer */}
      <IncidentHistoryDrawer
        open={showHistoryDrawer}
        loading={historyLoading}
        employeeName={historyEmployeeName}
        year={historyYear}
        data={historyIncidents}
        onChange={handleHistoryTableChange}
        paginationConfig={{
          current: historyPage,
          total: historyData?.attendanceIncidents?.count,
          pageSize: historyPageSize,
        }}
        onClose={() => {
          setShowHistoryDrawer(false);
          setHistoryPage(1);
          setHistoryPageSize(LIMIT);
        }}
        onEdit={handleOpenManage}
        canUpdate={isUpdatePermission}
        currentUserId={currentUserId}
        onBulkAction={handleBulkIncidentAction}
        bulkLoading={bulkUpdating}
      />
    </div>
  );
}

export default AttendanceTracker;
